/* Developer : Nithin
Date : 14-07-2026
changes: Commented out Next Stage column (dropdown) and added Lead Status column displayed as badge instead */
import React from 'react';
import { LEAD_STAGES } from '@/constants/globalConstants';
import { LEAD_EXECUTION_STAGES } from './pipline';

export const getLeadExecutionFinancialColumns = ({ onStageChange } = {}) => {
  return [
    {
      header: "Start date",
      key: "startDate",
      minWidth: "100px",
      render: (row) => {
        const dateToUse = row.createdAt || row.startDate;
        return (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {dateToUse ? new Date(dateToUse).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        );
      }
    },
    {
      header: "Project value (₹)",
      key: "projectValue",
      render: (row) => {
        const val = row.projectValue || row.leadId?.quotationAmount || row.leadId?.budget || 0;
        return (
          <span className="text-[11px] font-medium text-slate-700">
            ₹ {val.toLocaleString('en-IN')}
          </span>
        );
      }
    },
    {
      header: "Project expenses (₹)",
      key: "projectExpenses",
      render: (row) => (
        <span className="text-[11px] font-medium text-red-600">
          ₹ {row.projectExpenses?.toLocaleString('en-IN') || "0"}
        </span>
      )
    },
    {
      header: "Material Expenses (₹)",
      key: "materialExpenses",
      render: (row) => (
        <span className="text-[11px] font-medium text-amber-600">
          ₹ {row.materialExpenses?.toLocaleString('en-IN') || "0"}
        </span>
      )
    },
    {
      header: "Amount recieved (₹)",
      key: "amountReceived",
      render: (row) => (
        <span className="text-[11px] font-medium text-emerald-600">
          ₹ {row.amountReceived?.toLocaleString('en-IN') || "0"}
        </span>
      )
    },
    {
      header: "Pending balance (₹)",
      key: "pendingBalance",
      render: (row) => {
        const total = row.projectValue || row.leadId?.quotationAmount || row.leadId?.budget || 0;
        const rec = row.amountReceived || 0;
        const pending = total - rec;
        return (
          <span className="text-[11px] font-medium text-slate-600">
            ₹ {pending.toLocaleString('en-IN')}
          </span>
        );
      }
    },
    {
      header: "profit/loss (₹)",
      key: "profitLoss",
      render: (row) => {
        const rec = row.amountReceived || 0;
        const exp = (row.projectExpenses || 0) + (row.materialExpenses || 0);
        const pl = rec - exp;
        const isProfit = pl >= 0;
        return (
          <span className={`text-[11px] font-medium ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
            ₹ {pl.toLocaleString('en-IN')}
          </span>
        );
      }
    },
    /* Developer : Nithin
       Date : 14-07-2026
       changes: Added Lead Status column displayed using existing status badge UI (rounded-full with dot) right after profit/loss */
    {
      header: "Lead Status",
      key: "leadStatus",
      minWidth: "150px",
      render: (row) => {
        const statusKey = row.leadId?.leadStatus || row.status || row.projectStatus || 'new';
        const stageObj = LEAD_STAGES.find(
          s => s.key === statusKey || s.label?.toLowerCase() === statusKey?.toLowerCase()
        );

        const label = stageObj ? stageObj.label : statusKey.replace(/_/g, ' ');
        const color = stageObj?.color || "border-slate-200 bg-slate-50 text-slate-700";

        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap uppercase tracking-tighter ${color}`}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-current opacity-75" />
            {label}
          </span>
        );
      }
    },
    /*
    {
      header: "Site Exec",
      key: "projectManager",
      minWidth: "120px",
      render: (row) => {
        return row.projectManager ? (
          <div className="text-brand-dark text-[11px] truncate" title={row.projectManager.name || "Unknown"}>
            {row.projectManager.name || "Unknown"}
          </div>
        ) : <span className="text-[11px] text-slate-400">Unassigned</span>;
      }
    },
    */
    {
      header: "Site Status",
      key: "projectStatus",
      minWidth: "140px",
      render: (row) => {
        const status = row.projectStatus || row.status || 'Not started';
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap bg-slate-50 text-slate-700 border-slate-200 uppercase tracking-tighter`}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-400" />
            {status}
          </span>
        );
      }
    },
    {
      header: "Progress",
      key: "progress",
      minWidth: "150px",
      render: (row) => {
        const status = row.leadId?.leadStatus || row.status || 'new';
        let activeIdx = LEAD_EXECUTION_STAGES.findIndex(s => s.toLowerCase() === status.toLowerCase());
        if (activeIdx === -1) activeIdx = 0;
        const progress = Math.max(0, Math.round(((activeIdx + 1) / LEAD_EXECUTION_STAGES.length) * 100));
        
        return (
          <div className="flex items-center gap-2 w-full mt-1">
            <div className="flex-1 bg-slate-200 rounded-full h-1.5">
              <div 
                className="bg-brand-blue h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="text-[11px] font-medium text-slate-700 w-8">{progress}%</span>
          </div>
        );
      }
    },
    /* Developer : Nithin
       Date : 14-07-2026
       changes: Commented out Next Stage dropdown column as requested */
    /*
    {
      header: "Next Stage",
      key: "nextStage",
      minWidth: "170px",
      render: (row) => {
        const status = row.leadId?.leadStatus || row.projectStatus || row.status || 'new';
        
        const currentIdx = LEAD_STAGES.findIndex(s => s.key === status);

        return (
          <div className="w-[170px]" onClick={(e) => e.stopPropagation()}>
            <select
              value={status}
              onChange={(e) => {
                if (onStageChange) {
                  onStageChange(row._id, e.target.value);
                } else if (row.onStageChange) {
                  row.onStageChange(row._id, e.target.value);
                }
              }}
              className="w-full text-[10px] font-bold uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#2b448f] cursor-pointer hover:border-[#2b448f] transition-colors"
            >
              {LEAD_STAGES.some(s => s.key === status) ? null : (
                <option key={status} value={status} className="hidden">Update Stage</option>
              )}
              {LEAD_STAGES.map((s, idx) => {
                return (
                  <option key={s.key} value={s.key}>{s.label}</option>
                );
              })}
            </select>
          </div>
        );
      }
    },
    */
  ];
};
